import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, newIdempotencyKey } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, parseMoney, rate } from '../lib/format.ts';
import { Card, ErrorBanner, Money } from '../components/ui.tsx';

interface Category {
  revenue_category: string;
  account_number: string;
  account_name: string;
  department_name: string;
  counts_toward_minimum: boolean;
  tax_code: string | null;
  prices_include_tax: boolean;
  total_rate_bp: number;
}

interface Outlet {
  id: string;
  code: string;
  name: string;
}

interface AccountOption {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
}

interface DraftLine {
  key: string;
  description: string;
  quantity: string;
  amount: string;
  revenue_category: string;
}

const blankLine = (): DraftLine => ({
  key: crypto.randomUUID(),
  description: '',
  quantity: '1',
  amount: '',
  revenue_category: '',
});

export function PostChargePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currency, club } = useSession();

  const [accountId, setAccountId] = useState(searchParams.get('account') ?? '');
  const [accountSearch, setAccountSearch] = useState('');
  const [outletId, setOutletId] = useState('');
  const [reference, setReference] = useState('');
  const [gratuity, setGratuity] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);

  const accounts = useApi<{ accounts: AccountOption[] }>(
    `/v1/member-accounts?limit=50${accountSearch ? `&search=${encodeURIComponent(accountSearch)}` : ''}`,
  );
  const outlets = useApi<{ outlets: Outlet[] }>('/v1/outlets');
  const categories = useApi<{ categories: Category[] }>('/v1/revenue-categories');

  const catByName = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categories.data?.categories ?? []) {
      // Outlet-specific mappings can repeat a category; the first is enough
      // for a preview, and the server resolves the real one on post.
      if (!m.has(c.revenue_category)) m.set(c.revenue_category, c);
    }
    return m;
  }, [categories.data]);

  /*
   * Live preview of what the member will actually be charged.
   *
   * This matters most under exclusive (US-style) tax, where the quoted price
   * is not the total. A member finding that out on their statement instead of
   * at the till is how a club gets a phone call.
   *
   * It is an ESTIMATE and labelled as one: the server recomputes from the
   * rates in force on the capture date, which is the number that binds.
   */
  const preview = useMemo(() => {
    let net = 0;
    let tax = 0;
    let qualifying = 0;

    for (const l of lines) {
      const cat = catByName.get(l.revenue_category);
      const listed = parseMoney(l.amount, currency);
      const qty = Number(l.quantity);
      if (!cat || listed === null || !Number.isFinite(qty) || qty === 0) continue;

      const extended = Math.round(listed * qty);
      const rateBp = cat.total_rate_bp ?? 0;

      if (cat.prices_include_tax) {
        const lineNet = Math.round((extended * 10000) / (10000 + rateBp));
        net += lineNet;
        tax += extended - lineNet;
        if (cat.counts_toward_minimum) qualifying += lineNet;
      } else {
        net += extended;
        tax += Math.round((extended * rateBp) / 10000);
        if (cat.counts_toward_minimum) qualifying += extended;
      }
    }

    const grat = parseMoney(gratuity, currency) ?? 0;
    return { net, tax, gratuity: grat, total: net + tax + grat, qualifying };
  }, [lines, catByName, currency, gratuity]);

  const post = useMutation(async () => {
    const payload = {
      member_account_id: accountId,
      source_module: outlets.data?.outlets.find((o) => o.id === outletId)?.code.toLowerCase() ?? 'front_desk',
      source_reference: reference || undefined,
      outlet_id: outletId || undefined,
      gratuity_minor: parseMoney(gratuity, currency) ?? 0,
      lines: lines
        .filter((l) => l.revenue_category && l.description && l.amount)
        .map((l) => ({
          description: l.description,
          quantity: Number(l.quantity),
          unit_amount_minor: parseMoney(l.amount, currency)!,
          revenue_category: l.revenue_category,
        })),
    };
    // One key per submission attempt. A double-clicked button, a browser
    // retry, or a flaky connection all resolve to the same charge.
    return api<{ id: string }>('/v1/charges', {
      method: 'POST',
      body: payload,
      idempotencyKey: newIdempotencyKey('web'),
    });
  });

  const validLines = lines.filter((l) => l.revenue_category && l.description && l.amount);
  const canPost = accountId !== '' && validLines.length > 0 && !post.busy;

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  async function submit() {
    const result = await post.run();
    if (result) navigate(`/members/${accountId}`);
  }

  const selected = accounts.data?.accounts.find((a) => a.member_account_id === accountId);

  return (
    <>
      <h1 style={{ marginBottom: 16 }}>Post a Charge</h1>
      <ErrorBanner error={post.error} />

      <div className="grid-2">
        <div>
          <Card title="Member">
            <label className="field">
              <span>Find the member</span>
              <input
                placeholder="Name or account number"
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">Choose an account…</option>
                {(accounts.data?.accounts ?? []).map((a) => (
                  <option key={a.member_account_id} value={a.member_account_id}>
                    {a.display_name} · {a.account_number}
                    {a.status !== 'current' ? ` (${a.status.replace('_', ' ')})` : ''}
                  </option>
                ))}
              </select>
            </label>

            {selected && selected.status !== 'current' && (
              <div className="notice">
                This account is <strong>{selected.status.replace('_', ' ')}</strong>. A suspended
                account is refused at posting — the ledger enforces it, not this form.
              </div>
            )}

            <label className="field">
              <span>Outlet</span>
              <select value={outletId} onChange={(e) => setOutletId(e.target.value)}>
                <option value="">Front desk</option>
                {(outlets.data?.outlets ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Reference</span>
              <input
                placeholder="Ticket or order number"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <div className="hint">Appears on the member's statement.</div>
            </label>
          </Card>

          <Card title="What the Member Pays">
            <table className="data">
              <tbody>
                <tr>
                  <td>Net</td>
                  <td className="num">
                    <Money minor={preview.net} currency={currency} />
                  </td>
                </tr>
                <tr>
                  <td>
                    Tax
                    {club?.prices_include_tax && (
                      <span className="muted small"> · included in prices</span>
                    )}
                  </td>
                  <td className="num">
                    <Money minor={preview.tax} currency={currency} />
                  </td>
                </tr>
                <tr>
                  <td>Service charge</td>
                  <td className="num">
                    <Money minor={preview.gratuity} currency={currency} />
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 650 }}>Total</td>
                  <td className="num" style={{ fontWeight: 650, fontSize: 16 }}>
                    <Money minor={preview.total} currency={currency} />
                  </td>
                </tr>
                <tr>
                  <td className="muted small">Counts toward F&amp;B minimum</td>
                  <td className="num muted small">{money(preview.qualifying, currency)}</td>
                </tr>
              </tbody>
            </table>
            <div className="hint">
              An estimate. The ledger recomputes tax from the rates in force on the capture date,
              and that is the figure that binds.
            </div>
          </Card>
        </div>

        <div>
          <Card
            title="Items"
            actions={
              <button className="secondary small" onClick={() => setLines((l) => [...l, blankLine()])}>
                Add item
              </button>
            }
          >
            {lines.map((line, i) => {
              const cat = catByName.get(line.revenue_category);
              return (
                <div
                  key={line.key}
                  style={{
                    borderTop: i > 0 ? '1px solid var(--hairline)' : undefined,
                    paddingTop: i > 0 ? 14 : 0,
                    marginTop: i > 0 ? 14 : 0,
                  }}
                >
                  <label className="field">
                    <span>Description</span>
                    <input
                      value={line.description}
                      placeholder="Clubhouse burger"
                      onChange={(e) => updateLine(line.key, { description: e.target.value })}
                    />
                  </label>

                  <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <label className="field" style={{ width: 84 }}>
                      <span>Qty</span>
                      <input
                        value={line.quantity}
                        inputMode="decimal"
                        onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                      />
                    </label>
                    <label className="field" style={{ flex: 1 }}>
                      <span>Unit price</span>
                      <input
                        value={line.amount}
                        inputMode="decimal"
                        placeholder="18.00"
                        onChange={(e) => updateLine(line.key, { amount: e.target.value })}
                      />
                      {line.amount && parseMoney(line.amount, currency) === null && (
                        <div className="hint neg">
                          Not a valid amount for {currency.code} — {currency.exponent} decimal
                          place{currency.exponent === 1 ? '' : 's'}.
                        </div>
                      )}
                    </label>
                  </div>

                  <label className="field">
                    <span>Category</span>
                    <select
                      value={line.revenue_category}
                      onChange={(e) => updateLine(line.key, { revenue_category: e.target.value })}
                    >
                      <option value="">Choose…</option>
                      {[...catByName.values()].map((c) => (
                        <option key={c.revenue_category} value={c.revenue_category}>
                          {c.revenue_category} · {c.account_name}
                        </option>
                      ))}
                    </select>
                    {cat && (
                      <div className="hint">
                        Posts to {cat.account_number} {cat.account_name} · {cat.department_name}
                        {cat.total_rate_bp > 0
                          ? ` · ${rate(cat.total_rate_bp)} ${cat.prices_include_tax ? 'included' : 'added'}`
                          : ' · no tax'}
                        {cat.counts_toward_minimum ? ' · counts toward the minimum' : ''}
                      </div>
                    )}
                  </label>

                  {lines.length > 1 && (
                    <button
                      className="secondary small"
                      onClick={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}
                    >
                      Remove item
                    </button>
                  )}
                </div>
              );
            })}
          </Card>

          <Card title="Service Charge">
            <label className="field">
              <span>Amount</span>
              <input
                value={gratuity}
                inputMode="decimal"
                placeholder="0.00"
                onChange={(e) => setGratuity(e.target.value)}
              />
              <div className="hint">
                A mandatory service charge is club revenue. It does not count toward the
                food &amp; beverage minimum.
              </div>
            </label>
          </Card>

          <div className="actions">
            <button onClick={submit} disabled={!canPost} className="gold">
              {post.busy ? 'Posting…' : `Post ${money(preview.total, currency)}`}
            </button>
            <button className="secondary" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
